import { useEffect, useLayoutEffect, useRef } from 'react';
import { ClockIcon } from '@phosphor-icons/react';
import { EmptyState, Spinner, useReducedMotion, useToast } from '@smworks-cz/ui-kit';
import type { AttachmentDTO, ConversationDTO, MeDTO } from '@sam/shared';
import { Composer, type ComposerHandle } from '../components/Composer';
import { AssistantMessage, UserMessage } from '../components/Message';
import { keys, queryClient, useConversation } from '../lib/queries';
import { followTurn, useAnyLiveTurn } from '../lib/turns';
import { sendMessage } from '../lib/send';
import { playComposerMove } from '../lib/composerMove';
import { navigate } from '../lib/router';
import { ApiError } from '../lib/api';
import { TranscriptPanel } from '../components/TranscriptPanel';
import { closeTranscript, useTranscriptPanel } from '../lib/transcript';

export function Chat({ id, me }: { id: string; me: MeDTO }) {
  const panel = useTranscriptPanel();
  // Panel přepisu patří k chatu: při přechodu jinam se zavře.
  useEffect(() => closeTranscript, [id]);
  const { data, isLoading, error } = useConversation(id);
  // Otevřený výsledek naplánované úlohy už není nepřečtený (backend to zapíše při načtení chatu).
  useEffect(() => {
    if (data?.id) queryClient.setQueryData<ConversationDTO[]>(keys.conversations, (old) => old?.map((c) => (c.id === data.id && c.unread ? { ...c, unread: false } : c)));
  }, [data?.id]);
  const busy = useAnyLiveTurn(id);
  const composer = useRef<ComposerHandle>(null);
  const scroller = useRef<HTMLDivElement>(null);
  const stick = useRef(true);
  const reduce = useReducedMotion();
  const { toast } = useToast();

  // Po obnovení stránky se znovu napojit na rozběhnutý tah.
  useEffect(() => {
    if (!data?.activeTurnId) return;
    const m = data.messages.find((x) => x.turnId === data.activeTurnId && x.role === 'assistant');
    if (m && m.status === 'streaming') followTurn({ turnId: data.activeTurnId, conversationId: id, messageId: m.id });
  }, [data?.activeTurnId, data?.messages, id]);

  useLayoutEffect(() => {
    playComposerMove(composer.current?.el() ?? null, reduce);
  }, [reduce]);

  // Držet se dole, dokud uživatel sám neodscrolluje nahoru.
  useEffect(() => {
    const el = scroller.current;
    if (!el) return;
    const onScroll = () => {
      stick.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
    };
    el.addEventListener('scroll', onScroll);
    const obs = new ResizeObserver(() => {
      if (stick.current) el.scrollTop = el.scrollHeight;
    });
    if (el.firstElementChild) obs.observe(el.firstElementChild);
    return () => {
      el.removeEventListener('scroll', onScroll);
      obs.disconnect();
    };
  }, [data?.id]);

  useEffect(() => {
    stick.current = true;
    const el = scroller.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [id]);

  useEffect(() => {
    if (data) document.title = `${data.title} · SAM`;
    return () => {
      document.title = 'SAM';
    };
  }, [data?.title]);

  const onSend = async (text: string, attachments: AttachmentDTO[]) => {
    try {
      stick.current = true;
      await sendMessage(id, text, attachments);
      return true;
    } catch (e) {
      toast({ variant: 'error', title: (e as Error).message });
      return false;
    }
  };

  if (error) {
    const notFound = error instanceof ApiError && error.status === 404;
    return (
      <div className="page-center">
        <EmptyState
          preset={notFound ? 'no-results' : 'error'}
          title={notFound ? 'Chat neexistuje' : 'Chat nejde načíst'}
          description={notFound ? 'Mohl být smazán, nebo patří někomu jinému.' : (error as Error).message}
          action={
            <button type="button" className="link-btn" onClick={() => navigate('/')}>
              Zpět na úvod
            </button>
          }
        />
      </div>
    );
  }

  return (
    <div className={`chat-layout${panel.ref ? ' with-panel' : ''}`}>
      <div className="chat">
        <div className="msgs" ref={scroller}>
          <div className="msgs-in">
          {data?.scheduleId && (
            <div className="chat-banner">
              <ClockIcon size={16} />
              <span>Výsledek naplánované úlohy. Upravit nebo vypnout ji jde v Nastavení → Naplánované úlohy.</span>
            </div>
          )}
            {isLoading && <Spinner />}
            {data?.messages.map((m) => (m.role === 'user' ? <UserMessage key={m.id} m={m} /> : <AssistantMessage key={m.id} m={m} />))}
          </div>
        </div>
        <div className="dock">
          <Composer ref={composer} placeholder="Odpovězte Samovi…" onSend={onSend} busy={busy} disabled={me.ai !== 'ok' || !data} autoFocus />
          <p className="disc">{me.ai !== 'ok' ? 'AI je teď nedostupná.' : 'Sam může dělat chyby. Důležité údaje si ověřte ve zdroji.'}</p>
        </div>
      </div>
      <TranscriptPanel />
    </div>
  );
}
